'use client'

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import dashboardService from '@/services/DashboardService'
import { portfolioService } from '@/services/portfolioService'
import { marketService } from '@/services/marketService'
import { riskService } from '@/services/riskService'
import { complianceService } from '@/services/complianceService'
import { quantumService } from '@/services/quantumService'
import { SECEdgarService, FILING_TYPES } from '@/lib/api/sec-edgar'
import type { SECFiling } from '@/lib/api/sec-edgar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import {
  TrendingUp, TrendingDown, DollarSign, Building, Users, Shield,
  Activity, AlertTriangle, RefreshCw, Settings, Download, Bell,
  ArrowUp, ArrowDown, Atom, Brain, Zap, Lock, Globe, BarChart3,
  PieChart, LineChart, Target, Briefcase, FileText, Calendar,
  Clock, Wifi, WifiOff, Database, Server, Cpu, HardDrive,
  MapPin, Home, Factory, ShoppingCart, Warehouse, Hotel,
  TrendingUpIcon, Eye, AlertCircle, CheckCircle, XCircle,
  Star, Award, Gauge, Layers, Network, Radar, Compass
} from 'lucide-react'
import { 
  AreaChart, Area, BarChart, Bar, LineChart as RechartsLineChart, Line, 
  PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, RadialBarChart, RadialBar,
  ComposedChart, Scatter, ScatterChart
} from 'recharts'

// Enhanced Types for Institutional Platform
interface InstitutionalMetric {
  id: string
  title: string
  value: string | number
  change: number
  changeType: 'increase' | 'decrease' | 'neutral'
  icon: any
  color: string
  status: 'excellent' | 'good' | 'warning' | 'critical'
  description: string
  confidence?: number
  benchmark?: string
  aiInsight?: string
  category: 'portfolio' | 'market' | 'risk' | 'quantum' | 'esg' | 'performance'
  unit?: string
  target?: number
  ytdChange?: number
  volatility?: number
}

interface MarketAlert {
  id: string
  type: 'opportunity' | 'risk' | 'market' | 'regulatory' | 'quantum'
  title: string
  description: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  timestamp: string
  location?: string
  impact: 'positive' | 'negative' | 'neutral'
  confidence: number
  aiGenerated: boolean
}

interface PortfolioAllocation {
  name: string
  value: number
  percentage: number
  color: string
  properties: number
  avgCapRate: number
  occupancy: number
}

interface GeographicDistribution {
  region: string
  value: number
  properties: number
  growth: number
  riskScore: number
  coordinates: [number, number]
}

interface PerformanceAttribution {
  factor: string
  contribution: number
  impact: 'positive' | 'negative'
  description: string
}

interface EnrichedSECFiling extends SECFiling {
  company?: string
  cik?: string
  form?: string
  description?: string
}

interface DashboardData {
  metrics: InstitutionalMetric[]
  alerts: MarketAlert[]
  activities: any[]
  portfolioData: any
  trends: any
  insights: any
  portfolioAnalytics: any
  marketIntelligence: any
  riskMetrics: any
  complianceStatus: any
  quantumSecurity: any
  widgets: any[]
  portfolioAllocation: PortfolioAllocation[]
  geographicDistribution: GeographicDistribution[]
  performanceAttribution: PerformanceAttribution[]
  marketTrends: any[]
  aiInsights: any[]
  executiveSummary: any
}

const UserDashboard: React.FC = () => {
  const { user, isAuthenticated, token } = useAuth()
  const [mounted, setMounted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d')
  const [selectedView, setSelectedView] = useState('executive')
  const [selectedMetricCategory, setSelectedMetricCategory] = useState('all')
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected')
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [secFilings, setSecFilings] = useState<EnrichedSECFiling[]>([])
  const [secFilingsLoading, setSecFilingsLoading] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttempts = useRef<number>(0)

  // Enhanced Dashboard Data State
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    metrics: [],
    alerts: [],
    activities: [],
    portfolioData: null,
    trends: null,
    insights: null,
    portfolioAnalytics: null,
    marketIntelligence: null,
    riskMetrics: null,
    complianceStatus: null,
    quantumSecurity: null,
    widgets: [],
    portfolioAllocation: [],
    geographicDistribution: [],
    performanceAttribution: [],
    marketTrends: [],
    aiInsights: [],
    executiveSummary: null
  })

  // Executive Summary Data
  const executiveSummary = {
    keyHighlights: [
      "Portfolio performance exceeded benchmark by 3.2% YTD",
      "Successfully deployed $847M in new acquisitions across 4 markets",
      "Quantum security protocols achieved 99.97% uptime",
      "AI-driven risk models prevented $23M in potential losses",
      "ESG compliance rating improved to AAA across all holdings"
    ],
    performanceDrivers: [
      "Strategic positioning in high-growth logistics markets",
      "Proactive asset management and value-add initiatives",
      "Diversified geographic exposure reducing concentration risk",
      "Advanced AI analytics optimizing investment decisions",
      "Strong tenant relationships and lease renewal rates"
    ]
  }

  // Sample institutional-grade data
  const institutionalMetrics: InstitutionalMetric[] = [
    {
      id: 'aum',
      title: 'Assets Under Management',
      value: '$2.847B',
      change: 12.3,
      changeType: 'increase',
      icon: DollarSign,
      color: 'blue',
      status: 'excellent',
      description: 'Total assets under management across all portfolios',
      confidence: 98.5,
      benchmark: '$2.5B Target',
      category: 'portfolio',
      unit: 'USD',
      target: 2500000000,
      ytdChange: 18.7,
      volatility: 8.2,
      aiInsight: 'AUM growth accelerating due to strategic acquisitions in high-growth markets. Projected to reach $3.2B by Q4 2024.'
    },
    {
      id: 'irr',
      title: 'Net IRR',
      value: '14.7%',
      change: 2.1,
      changeType: 'increase',
      icon: TrendingUp,
      color: 'green',
      status: 'excellent',
      description: 'Net internal rate of return across all investments',
      confidence: 94.2,
      benchmark: '12% Target',
      category: 'performance',
      unit: '%',
      target: 12,
      ytdChange: 3.4,
      volatility: 4.1,
      aiInsight: 'IRR outperforming benchmark by 270bps. Value-add strategies driving superior returns.'
    },
    {
      id: 'moic',
      title: 'MOIC',
      value: '2.34x',
      change: 0.18,
      changeType: 'increase',
      icon: Target,
      color: 'purple',
      status: 'excellent',
      description: 'Multiple on invested capital',
      confidence: 96.1,
      benchmark: '2.0x Target',
      category: 'performance',
      unit: 'x',
      target: 2.0,
      ytdChange: 0.24,
      volatility: 0.12,
      aiInsight: 'MOIC exceeding targets across all vintage years. Strong exit performance driving multiples.'
    },
    {
      id: 'occupancy',
      title: 'Portfolio Occupancy',
      value: '94.2%',
      change: 1.8,
      changeType: 'increase',
      icon: Building,
      color: 'orange',
      status: 'excellent',
      description: 'Weighted average occupancy across all properties',
      confidence: 99.1,
      benchmark: '90% Target',
      category: 'portfolio',
      unit: '%',
      target: 90,
      ytdChange: 2.3,
      volatility: 1.2,
      aiInsight: 'Occupancy rates at historic highs. Tenant retention strategies proving highly effective.'
    },
    {
      id: 'leverage',
      title: 'Portfolio LTV',
      value: '62.4%',
      change: -1.2,
      changeType: 'decrease',
      icon: Gauge,
      color: 'yellow',
      status: 'good',
      description: 'Loan-to-value ratio across portfolio',
      confidence: 97.8,
      benchmark: '65% Target',
      category: 'risk',
      unit: '%',
      target: 65,
      ytdChange: -2.1,
      volatility: 2.8,
      aiInsight: 'LTV trending down due to property appreciation and debt paydown. Optimal capital structure maintained.'
    },
    {
      id: 'noi-growth',
      title: 'NOI Growth',
      value: '8.9%',
      change: 1.4,
      changeType: 'increase',
      icon: TrendingUpIcon,
      color: 'green',
      status: 'excellent',
      description: 'Net operating income growth year-over-year',
      confidence: 93.7,
      benchmark: '6% Target',
      category: 'performance',
      unit: '%',
      target: 6,
      ytdChange: 2.1,
      volatility: 3.4,
      aiInsight: 'NOI growth driven by rent increases and operational efficiencies. Outpacing inflation by 350bps.'
    },
    {
      id: 'esg-score',
      title: 'ESG Score',
      value: '87.3',
      change: 3.2,
      changeType: 'increase',
      icon: Layers,
      color: 'green',
      status: 'excellent',
      description: 'Environmental, Social, Governance composite score',
      confidence: 91.4,
      benchmark: '80 Target',
      category: 'esg',
      unit: 'Score',
      target: 80,
      ytdChange: 4.7,
      volatility: 2.1,
      aiInsight: 'ESG initiatives driving tenant satisfaction and premium valuations. Top quartile performance.'
    },
    {
      id: 'quantum-security',
      title: 'Quantum Security',
      value: '99.8%',
      change: 0.1,
      changeType: 'increase',
      icon: Atom,
      color: 'blue',
      status: 'excellent',
      description: 'Quantum-secured transaction and data protection',
      confidence: 99.9,
      benchmark: '99% Target',
      category: 'quantum',
      unit: '%',
      target: 99,
      ytdChange: 0.2,
      volatility: 0.1,
      aiInsight: 'Quantum security protocols operating at maximum efficiency. Zero security breaches detected.'
    },
    {
      id: 'ai-confidence',
      title: 'AI Prediction Confidence',
      value: '96.4%',
      change: 2.1,
      changeType: 'increase',
      icon: Brain,
      color: 'purple',
      status: 'excellent',
      description: 'AI model prediction accuracy and confidence',
      confidence: 96.4,
      benchmark: '90% Target',
      category: 'performance',
      unit: '%',
      target: 90,
      ytdChange: 3.2,
      volatility: 1.8,
      aiInsight: 'AI models achieving exceptional accuracy. Machine learning algorithms continuously improving.'
    },
    {
      id: 'market-alpha',
      title: 'Market Alpha',
      value: '+4.7%',
      change: 0.8,
      changeType: 'increase',
      icon: Zap,
      color: 'gold',
      status: 'excellent',
      description: 'Excess return vs market benchmark',
      confidence: 92.1,
      benchmark: '+3% Target',
      category: 'performance',
      unit: '%',
      target: 3,
      ytdChange: 1.2,
      volatility: 2.4,
      aiInsight: 'Generating significant alpha through AI-driven investment strategies and market timing.'
    },
    {
      id: 'quantum-security',
      title: 'Quantum Security',
      value: '99.9%',
      change: 0.3,
      changeType: 'increase' as const,
      icon: Atom,
      color: 'purple',
      status: 'excellent',
      description: 'Quantum encryption and security status',
      confidence: 99.9,
      benchmark: '99% Target',
      category: 'quantum',
      unit: '%',
      target: 99,
      ytdChange: 0.3,
      volatility: 0.1,
      aiInsight: 'Quantum security infrastructure operating at peak efficiency. Zero security incidents detected.'
    },
    {
      id: 'ai-accuracy',
      title: 'AI Model Accuracy',
      value: '96.4%',
      change: 0.8,
      changeType: 'increase',
      icon: Brain,
      color: 'purple',
      status: 'excellent',
      description: 'AI prediction model accuracy across all use cases',
      confidence: 98.2,
      benchmark: '90% Target',
      category: 'quantum',
      unit: '%',
      target: 90,
      ytdChange: 2.1,
      volatility: 1.4,
      aiInsight: 'AI models continuously improving through reinforcement learning. Prediction accuracy at industry-leading levels.'
    },
    {
      id: 'market-opportunities',
      title: 'Market Opportunities',
      value: '147',
      change: 23,
      changeType: 'increase',
      icon: Eye,
      color: 'blue',
      status: 'excellent',
      description: 'AI-identified investment opportunities',
      confidence: 89.3,
      benchmark: '100 Target',
      category: 'market',
      unit: 'Count',
      target: 100,
      ytdChange: 47,
      volatility: 12.3,
      aiInsight: 'Market opportunity pipeline at record levels. Focus on industrial and multifamily sectors.'
    },
    {
      id: 'risk-score',
      title: 'Portfolio Risk Score',
      value: '6.8/10',
      change: -0.3,
      changeType: 'decrease',
      icon: Shield,
      color: 'yellow',
      status: 'good',
      description: 'Composite risk score (lower is better)',
      confidence: 95.1,
      benchmark: '7.0 Target',
      category: 'risk',
      unit: 'Score',
      target: 7.0,
      ytdChange: -0.7,
      volatility: 0.4,
      aiInsight: 'Risk profile improving through diversification and active management. Well within target parameters.'
    },
    {
      id: 'transaction-volume',
      title: 'Transaction Volume',
      value: '$847M',
      change: 34.2,
      changeType: 'increase',
      icon: Activity,
      color: 'green',
      status: 'excellent',
      description: 'Total transaction volume year-to-date',
      confidence: 97.6,
      benchmark: '$600M Target',
      category: 'market',
      unit: 'USD',
      target: 600000000,
      ytdChange: 41.2,
      volatility: 18.7,
      aiInsight: 'Transaction activity accelerating. Strategic acquisitions in high-growth markets driving volume.'
    }
  ]

  // Enhanced Portfolio Allocation Data
  const portfolioAllocation: PortfolioAllocation[] = [
    { name: 'Office', value: 1247000000, percentage: 43.8, color: '#3B82F6', properties: 127, avgCapRate: 6.2, occupancy: 94.1 },
    { name: 'Multifamily', value: 854000000, percentage: 30.0, color: '#10B981', properties: 89, avgCapRate: 5.8, occupancy: 96.3 },
    { name: 'Industrial', value: 398000000, percentage: 14.0, color: '#F59E0B', properties: 45, avgCapRate: 7.1, occupancy: 98.2 },
    { name: 'Retail', value: 227000000, percentage: 8.0, color: '#EF4444', properties: 34, avgCapRate: 6.8, occupancy: 89.7 },
    { name: 'Hospitality', value: 121000000, percentage: 4.2, color: '#8B5CF6', properties: 12, avgCapRate: 8.4, occupancy: 87.5 }
  ]

  // Geographic Distribution Data
  const geographicDistribution: GeographicDistribution[] = [
    { region: 'New York Metro', value: 712000000, properties: 78, growth: 12.4, riskScore: 7.2, coordinates: [-74.0060, 40.7128] },
    { region: 'San Francisco Bay', value: 568000000, properties: 52, growth: 18.7, riskScore: 8.1, coordinates: [-122.4194, 37.7749] },
    { region: 'Los Angeles', value: 445000000, properties: 67, growth: 9.8, riskScore: 6.9, coordinates: [-118.2437, 34.0522] },
    { region: 'Chicago', value: 334000000, properties: 43, growth: 7.2, riskScore: 5.8, coordinates: [-87.6298, 41.8781] },
    { region: 'Miami', value: 298000000, properties: 38, growth: 15.3, riskScore: 7.8, coordinates: [-80.1918, 25.7617] },
    { region: 'Austin', value: 267000000, properties: 29, growth: 22.1, riskScore: 6.4, coordinates: [-97.7431, 30.2672] },
    { region: 'Seattle', value: 223000000, properties: 31, growth: 14.6, riskScore: 7.0, coordinates: [-122.3321, 47.6062] }
  ]

  // Performance Attribution Data
  const performanceAttribution: PerformanceAttribution[] = [
    { factor: 'Asset Selection', contribution: 4.2, impact: 'positive', description: 'Superior property selection driving outperformance' },
    { factor: 'Market Timing', contribution: 2.1, impact: 'positive', description: 'Strategic entry and exit timing' },
    { factor: 'Operational Excellence', contribution: 1.8, impact: 'positive', description: 'Best-in-class property management' },
    { factor: 'Capital Structure', contribution: 1.3, impact: 'positive', description: 'Optimal leverage and financing' },
    { factor: 'ESG Initiatives', contribution: 0.9, impact: 'positive', description: 'Sustainability driving premium valuations' },
    { factor: 'Market Headwinds', contribution: -0.7, impact: 'negative', description: 'Broader market volatility impact' }
  ]

  // Market Trends Data
  const marketTrends = [
    { month: 'Jan', capRates: 6.2, occupancy: 92.1, noi: 108.3, values: 245.7 },
    { month: 'Feb', capRates: 6.1, occupancy: 92.8, noi: 110.2, values: 248.9 },
    { month: 'Mar', capRates: 6.0, occupancy: 93.4, noi: 112.1, values: 252.3 },
    { month: 'Apr', capRates: 5.9, occupancy: 93.9, noi: 114.5, values: 256.1 },
    { month: 'May', capRates: 5.8, occupancy: 94.2, noi: 116.8, values: 259.8 },
    { month: 'Jun', capRates: 5.7, occupancy: 94.6, noi: 119.2, values: 263.7 }
  ]

  // AI Insights Data
  const aiInsights = [
    {
      category: 'Market Opportunity',
      insight: 'Industrial properties in Austin showing 23% price appreciation potential',
      confidence: 94.2,
      timeframe: '12-18 months',
      impact: 'High',
      recommendation: 'Increase allocation to Austin industrial by 15%'
    },
    {
      category: 'Risk Alert',
      insight: 'Office sector in SF showing early signs of oversupply',
      confidence: 87.6,
      timeframe: '6-12 months',
      impact: 'Medium',
      recommendation: 'Consider reducing office exposure by 8%'
    },
    {
      category: 'ESG Opportunity',
      insight: 'Green building certifications driving 12% rent premiums',
      confidence: 91.3,
      timeframe: '3-6 months',
      impact: 'Medium',
      recommendation: 'Accelerate ESG retrofit program'
    }
  ]

  // Enhanced Market Alerts
  const alerts: MarketAlert[] = [
    {
      id: '1',
      type: 'opportunity',
      title: 'Prime Office Asset Available - Manhattan',
      description: 'Class A office building with 94% occupancy and below-market pricing',
      priority: 'high',
      timestamp: '2024-01-15T10:30:00Z',
      location: 'Manhattan, NY',
      impact: 'positive',
      confidence: 92.4,
      aiGenerated: true
    },
    {
      id: '2',
      type: 'risk',
      title: 'Interest Rate Sensitivity Alert',
      description: 'Portfolio showing elevated sensitivity to rate changes',
      priority: 'medium',
      timestamp: '2024-01-15T09:15:00Z',
      impact: 'negative',
      confidence: 88.7,
      aiGenerated: true
    },
    {
      id: '3',
      type: 'market',
      title: 'Industrial Demand Surge - Southeast',
      description: 'Logistics demand driving industrial cap rate compression',
      priority: 'high',
      timestamp: '2024-01-15T08:45:00Z',
      location: 'Southeast US',
      impact: 'positive',
      confidence: 95.1,
      aiGenerated: true
    },
    {
      id: 'alert-2',
      type: 'risk',
      title: 'Market Volatility Alert',
      description: 'Increased volatility detected in commercial real estate markets',
      priority: 'high',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      location: 'Multiple Markets',
      impact: 'negative',
      confidence: 87.6,
      aiGenerated: true
    },
    {
      id: 'alert-3',
      type: 'market',
      title: 'Interest Rate Decision',
      description: 'Fed expected to hold rates steady, positive for real estate valuations',
      priority: 'medium',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      impact: 'positive',
      confidence: 78.9,
      aiGenerated: false
    },
    {
      id: 'alert-4',
      type: 'quantum',
      title: 'Security Scan Complete',
      description: 'Quantum security infrastructure scan completed successfully, no vulnerabilities detected',
      priority: 'low',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
      impact: 'positive',
      confidence: 99.8,
      aiGenerated: true
    }
  ]

  // WebSocket Connection Management
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    try {
      // CACHE BUSTER: Force browser to reload - Updated at 2024-10-05 22:30
      const wsUrl = 'wss://aisynergybridge.com/api/v1/ws/dashboard'
      
      console.log('WebSocket URL (FIXED):', wsUrl, 'NODE_ENV:', process.env.NODE_ENV)
      wsRef.current = new WebSocket(wsUrl)
      
      wsRef.current.onopen = () => {
        setConnectionStatus('connected')
        console.log('Dashboard WebSocket connected')
        
        // Reset reconnection attempts on successful connection
        reconnectAttempts.current = 0
        
        // Send authentication token
        if (token) {
          wsRef.current?.send(JSON.stringify({
            type: 'auth',
            token: token
          }))
        }
        
        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30000)
      }
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          
          switch (data.type) {
            case 'dashboard_update':
              setDashboardData(prev => ({
                ...prev,
                ...data.payload
              }))
              setLastUpdated(new Date())
              break
              
            case 'market_alert':
              setDashboardData(prev => ({
                ...prev,
                alerts: [data.payload, ...prev.alerts.slice(0, 9)]
              }))
              toast.info(`New Alert: ${data.payload.title}`)
              break
              
            case 'metric_update':
              setDashboardData(prev => ({
                ...prev,
                metrics: prev.metrics.map(metric => 
                  metric.id === data.payload.id 
                    ? { ...metric, ...data.payload }
                    : metric
                )
              }))
              break
              
            case 'pong':
              // Heartbeat response
              break
          }
        } catch (error) {
          console.error('WebSocket message parsing error:', error)
        }
      }
      
      wsRef.current.onclose = () => {
        setConnectionStatus('disconnected')
        console.log('Dashboard WebSocket disconnected')
        
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current)
          heartbeatRef.current = null
        }
        
        // Only attempt reconnection if autoRefresh is enabled and we haven't exceeded max attempts
        if (autoRefresh && reconnectAttempts.current < 5) {
          reconnectAttempts.current++
          console.log(`WebSocket reconnection attempt ${reconnectAttempts.current}/5`)
          setTimeout(() => {
            setConnectionStatus('reconnecting')
            connectWebSocket()
          }, 5000)
        } else if (reconnectAttempts.current >= 5) {
          console.log('Max WebSocket reconnection attempts reached. Stopping reconnection.')
          setConnectionStatus('disconnected')
        }
      }
      
      wsRef.current.onerror = (error) => {
        console.error('Dashboard WebSocket error:', error)
        setConnectionStatus('disconnected')
      }
      
    } catch (error) {
      console.error('WebSocket connection error:', error)
      setConnectionStatus('disconnected')
    }
  }, [token, autoRefresh])

  // Data Loading Functions
  const loadDashboardData = useCallback(async () => {
    if (!isAuthenticated || !token) return

    try {
      setLoading(true)
      setError(null)

      // Load all dashboard data in parallel
      const [
        metricsResponse,
        alertsResponse,
        portfolioResponse,
        marketResponse,
        riskResponse,
        complianceResponse,
        quantumResponse
      ] = await Promise.allSettled([
        dashboardService.getMetrics(selectedTimeframe),
        dashboardService.getAlerts(),
        portfolioService.getPortfolioAnalytics(),
        marketService.getMarketIntelligence(),
        riskService.getRiskMetrics(),
        complianceService.getComplianceStatus(),
        quantumService.getQuantumSecurity()
      ])

      // Process successful responses
      const newDashboardData: Partial<DashboardData> = {
        metrics: institutionalMetrics, // Use enhanced metrics
        alerts: alerts, // Use enhanced alerts
        portfolioAllocation,
        geographicDistribution,
        performanceAttribution,
        marketTrends,
        aiInsights
      }

      // Merge API responses if successful
      if (metricsResponse.status === 'fulfilled') {
        // Merge with API metrics if available
      }
      
      if (alertsResponse.status === 'fulfilled') {
        // Merge with API alerts if available
      }

      setDashboardData(prev => ({
        ...prev,
        ...newDashboardData
      }))

      setLastUpdated(new Date())
      
    } catch (error) {
      console.error('Dashboard data loading error:', error)
      setError('Failed to load dashboard data')
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, token, selectedTimeframe])

  // Refresh Data Function
  const refreshData = useCallback(async () => {
    setRefreshing(true)
    await loadDashboardData()
    setRefreshing(false)
    toast.success('Dashboard data refreshed')
  }, [loadDashboardData])

  // Generate Report Function
  const generateReport = useCallback(async () => {
    setIsGeneratingReport(true)
    try {
      const reportData = {
        metrics: dashboardData.metrics,
        portfolioAllocation: dashboardData.portfolioAllocation,
        geographicDistribution: dashboardData.geographicDistribution,
        performanceAttribution: dashboardData.performanceAttribution,
        aiInsights: dashboardData.aiInsights,
        timeframe: selectedTimeframe,
        generatedAt: new Date().toISOString()
      }
      
      // Call report generation API
      const response = await fetch('/api/v1/reports/executive-dashboard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reportData)
      })
      
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `AISB-Executive-Dashboard-${new Date().toISOString().split('T')[0]}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        toast.success('Executive report generated successfully')
      } else {
        throw new Error('Report generation failed')
      }
    } catch (error) {
      console.error('Report generation error:', error)
      toast.error('Failed to generate report')
    } finally {
      setIsGeneratingReport(false)
    }
  }, [dashboardData, selectedTimeframe, token])

  // Filter metrics based on category
  const filteredMetrics = useMemo(() => {
    if (selectedMetricCategory === 'all') {
      return dashboardData.metrics
    }
    return dashboardData.metrics.filter(metric => 
      metric.category === selectedMetricCategory
    )
  }, [dashboardData.metrics, selectedMetricCategory])

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh || !isAuthenticated) return

    const interval = setInterval(() => {
      loadDashboardData()
    }, 60000) // Refresh every minute

    return () => clearInterval(interval)
  }, [autoRefresh, isAuthenticated]) // Removed loadDashboardData from dependencies to prevent infinite loop

  // Initial load and WebSocket connection
  useEffect(() => {
    setMounted(true)

    if (isAuthenticated) {
      loadDashboardData()
      connectWebSocket()
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current)
      }
    }
  }, [isAuthenticated]) // Removed loadDashboardData and connectWebSocket from dependencies to prevent infinite loop

  // Load SEC EDGAR recent filings
  useEffect(() => {
    async function loadSECFilings() {
      if (!isAuthenticated) return

      try {
        setSecFilingsLoading(true)

        // Get list of REITs to monitor
        const reitList = await SECEdgarService.getREITList(5)

        // Get recent filings for each REIT
        const allFilings: EnrichedSECFiling[] = []
        for (const reit of reitList.reits.slice(0, 3)) { // Monitor top 3 REITs
          try {
            const filings = await SECEdgarService.getRecentFilings(reit.cik, undefined, 3)
            allFilings.push(...filings.filings.map(f => ({
              ...f,
              company: reit.name,
              cik: reit.cik,
              form: f.form_type,
              description: f.primary_doc_description || f.items || 'SEC Filing'
            } as EnrichedSECFiling)))
          } catch (err) {
            console.warn(`Failed to load filings for ${reit.ticker}:`, err)
          }
        }

        // Sort by date and take latest 10
        allFilings.sort((a, b) => new Date(b.filing_date).getTime() - new Date(a.filing_date).getTime())
        setSecFilings(allFilings.slice(0, 10))

      } catch (err) {
        console.error('Failed to load SEC filings:', err)
      } finally {
        setSecFilingsLoading(false)
      }
    }

    loadSECFilings()
  }, [isAuthenticated])

  // Helper functions
  const getConnectionStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'text-emerald-400'
      case 'reconnecting': return 'text-amber-400'
      default: return 'text-red-400'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-emerald-400 bg-emerald-500/[0.06]'
      case 'good': return 'text-amber-400 bg-amber-500/[0.08]'
      case 'warning': return 'text-amber-400 bg-amber-500/[0.08]'
      case 'critical': return 'text-red-400 bg-red-500/[0.06]'
      default: return 'text-zinc-400 bg-zinc-900/40'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/[0.10] text-red-300 border-red-500/30'
      case 'high': return 'bg-amber-500/[0.10] text-amber-200 border-amber-500/30'
      case 'medium': return 'bg-amber-500/[0.10] text-amber-200 border-amber-500/30'
      case 'low': return 'bg-emerald-500/[0.10] text-emerald-300 border-emerald-500/30'
      default: return 'bg-zinc-900/60 text-zinc-200 border-zinc-800'
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)
  }

  // Manual refresh handler
  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true)
    await loadDashboardData()
    
    // Send refresh signal via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'refresh_request',
        timestamp: new Date().toISOString()
      }))
    }
    
    setRefreshing(false)
    toast.success('Dashboard refreshed')
  }, [loadDashboardData])

  if (!mounted) {
    return null
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="mx-auto h-12 w-12 text-zinc-500" />
              <h3 className="mt-2 text-sm font-medium text-zinc-100">Access Denied</h3>
              <p className="mt-1 text-sm text-zinc-500">
                Please log in to access the institutional dashboard.
              </p>
              <div className="mt-6">
                <Button onClick={() => window.location.href = '/login'}>
                  Go to Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-zinc-100">Error Loading Dashboard</h3>
              <p className="mt-1 text-sm text-zinc-500">{error}</p>
              <div className="mt-6">
                <Button onClick={loadDashboardData}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div
      className="dark min-h-screen bg-zinc-950 text-zinc-100"
      style={{
        // Force sovereign palette on shadcn token-based components.
        // Bypasses parent ThemeProvider's light theme so Card/Tabs/Select/Badge
        // render correctly regardless of next-themes' html-class state.
        ['--background' as any]: '240 5% 6%',
        ['--foreground' as any]: '0 0% 98%',
        ['--card' as any]: '240 4% 11%',
        ['--card-foreground' as any]: '0 0% 98%',
        ['--popover' as any]: '240 4% 11%',
        ['--popover-foreground' as any]: '0 0% 98%',
        ['--primary' as any]: '38 92% 50%',
        ['--primary-foreground' as any]: '240 5% 6%',
        ['--secondary' as any]: '240 4% 16%',
        ['--secondary-foreground' as any]: '0 0% 98%',
        ['--muted' as any]: '240 4% 16%',
        ['--muted-foreground' as any]: '240 5% 65%',
        ['--accent' as any]: '38 92% 50%',
        ['--accent-foreground' as any]: '240 5% 6%',
        ['--destructive' as any]: '0 84% 60%',
        ['--destructive-foreground' as any]: '0 0% 98%',
        ['--border' as any]: '240 4% 16%',
        ['--input' as any]: '240 4% 16%',
        ['--ring' as any]: '38 92% 50%',
      }}
    >
      {/* Enhanced Header */}
      <div className="sticky top-0 z-50 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Atom className="h-8 w-8 text-amber-400" />
                <h1 className="text-xl font-bold text-zinc-100">
                  AI Synergy Bridge
                </h1>
              </div>
              <Badge variant="outline" className="hidden sm:flex">
                $2.847B AUM • Institutional Platform
              </Badge>
            </div>

            <div className="flex items-center space-x-4">
              {/* Connection Status */}
              <div className="flex items-center space-x-2">
                {connectionStatus === 'connected' ? (
                  <Wifi className={`h-4 w-4 ${getConnectionStatusColor(connectionStatus)}`} />
                ) : (
                  <WifiOff className={`h-4 w-4 ${getConnectionStatusColor(connectionStatus)}`} />
                )}
                <span className={`text-xs font-medium ${getConnectionStatusColor(connectionStatus)}`}>
                  {connectionStatus.charAt(0).toUpperCase() + connectionStatus.slice(1)}
                </span>
              </div>

              {/* Last Updated */}
              <div className="hidden sm:flex items-center space-x-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{lastUpdated.toLocaleTimeString()}</span>
              </div>

              {/* Auto Refresh Toggle */}
              <div className="flex items-center space-x-2">
                <Label htmlFor="auto-refresh" className="text-xs">Auto</Label>
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
              </div>

              {/* Manual Refresh */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="h-8 w-8 p-0"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>

              {/* Generate Report */}
              <Button
                variant="ghost"
                size="sm"
                onClick={generateReport}
                disabled={isGeneratingReport}
                className="hidden sm:flex"
              >
                <Download className="mr-2 h-4 w-4" />
                {isGeneratingReport ? 'Generating...' : 'Report'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={selectedView} onValueChange={setSelectedView} className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <TabsList className="grid w-full sm:w-auto grid-cols-3 sm:grid-cols-7">
              <TabsTrigger value="executive">Executive</TabsTrigger>
              <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="risk">Risk</TabsTrigger>
              <TabsTrigger value="market">Market</TabsTrigger>
              <TabsTrigger value="quantum">Quantum</TabsTrigger>
              <TabsTrigger value="detailed">Detailed</TabsTrigger>
            </TabsList>

            <div className="flex items-center space-x-4">
              <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1d">1 Day</SelectItem>
                  <SelectItem value="7d">7 Days</SelectItem>
                  <SelectItem value="30d">30 Days</SelectItem>
                  <SelectItem value="90d">90 Days</SelectItem>
                  <SelectItem value="1y">1 Year</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedMetricCategory} onValueChange={setSelectedMetricCategory}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Metrics</SelectItem>
                  <SelectItem value="portfolio">Portfolio</SelectItem>
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="market">Market</SelectItem>
                  <SelectItem value="risk">Risk</SelectItem>
                  <SelectItem value="esg">ESG</SelectItem>
                  <SelectItem value="quantum">Quantum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Main Dashboard Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedView}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Executive Summary Tab */}
              <TabsContent value="executive" className="space-y-6">
                {/* Executive Summary */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Award className="h-6 w-6 text-amber-400" />
                      <span>Executive Summary</span>
                      <Badge className="ml-2 bg-amber-500">Q3 2024</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-emerald-300 mb-2">Key Highlights</h4>
                        <ul className="space-y-1">
                          {executiveSummary.keyHighlights.map((highlight, index) => (
                            <li key={index} className="flex items-start space-x-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-amber-300 mb-2">Performance Drivers</h4>
                        <ul className="space-y-1">
                          {executiveSummary.performanceDrivers.map((driver, index) => (
                            <li key={index} className="flex items-start space-x-2 text-sm">
                              <TrendingUp className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                              <span>{driver}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {filteredMetrics.slice(0, 8).map((metric) => {
                    const IconComponent = metric.icon
                    return (
                      <Card key={metric.id} className="relative overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105 border-l-4 border-l-amber-500">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium text-zinc-300">{metric.title}</CardTitle>
                          <div className="flex items-center space-x-2">
                            <Badge className={`text-xs px-2 py-1 rounded-full ${getStatusColor(metric.status)}`}>
                              {metric.status}
                            </Badge>
                            <IconComponent className="h-4 w-4 text-amber-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-zinc-100 mb-1">{metric.value}</div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                              {metric.changeType === 'increase' ? (
                                <ArrowUp className="h-3 w-3 text-emerald-400" />
                              ) : metric.changeType === 'decrease' ? (
                                <ArrowDown className="h-3 w-3 text-red-400" />
                              ) : (
                                <div className="h-3 w-3" />
                              )}
                              <span className={
                                metric.changeType === 'increase' ? 'text-emerald-400 font-medium' : 
                                metric.changeType === 'decrease' ? 'text-red-400 font-medium' : 'text-zinc-500'
                              }>
                                {Math.abs(metric.change)}%
                              </span>
                              <span>vs benchmark</span>
                            </div>
                            <div className="text-xs text-zinc-500">
                              {metric.unit}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-zinc-400 bg-zinc-900/40 p-2 rounded">
                            <strong>AI Insight:</strong> {metric.aiInsight}
                          </div>
                          <div className="mt-2 flex items-center justify-between text-xs">
                            <span className="text-zinc-500">Confidence: {metric.confidence}%</span>
                            <span className="text-zinc-500">Target: {metric.target}</span>
                          </div>
                          {metric.confidence && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs">
                                <span>Confidence</span>
                                <span>{metric.confidence}%</span>
                              </div>
                              <Progress value={metric.confidence} className="h-1 mt-1" />
                            </div>
                          )}
                          {metric.benchmark && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              Target: {metric.benchmark}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>

                {/* AI Insights */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Brain className="h-6 w-6 text-amber-400" />
                      <span>AI-Powered Market Intelligence</span>
                      <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Real-time</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {aiInsights.map((insight, index) => (
                        <div key={index} className="p-4 border-2 border-zinc-800/60 rounded-lg bg-zinc-900/60 shadow-none hover:shadow-none transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <Badge variant="outline" className="text-xs bg-amber-500/[0.06] text-amber-300 border-zinc-800">
                              {insight.category}
                            </Badge>
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center space-x-1">
                                <Zap className="h-3 w-3 text-amber-400" />
                                <span className="text-xs font-medium text-zinc-400">
                                  {insight.confidence}% confidence
                                </span>
                              </div>
                              <Badge className={
                                insight.impact === 'High' ? 'bg-red-500/[0.10] text-red-300 border-red-500/30' :
                                insight.impact === 'Medium' ? 'bg-amber-500/[0.10] text-amber-200 border-amber-500/30' :
                                'bg-emerald-500/[0.10] text-emerald-300 border-emerald-500/30'
                              }>
                                {insight.impact} Impact
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm font-medium mb-3 text-zinc-200">{insight.insight}</p>
                          <div className="text-xs text-amber-300 bg-amber-500/[0.08] p-3 rounded border-l-4 border-amber-500/40">
                            <div className="flex items-start space-x-2">
                              <Target className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <strong>AI Recommendation:</strong> {insight.recommendation}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Market Alerts */}
                <Card className="border-2 border-amber-500/30 bg-amber-500/[0.04]">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Bell className="h-6 w-6 text-amber-400" />
                      <span>Market Alerts & Opportunities</span>
                      <Badge className="bg-amber-500/[0.10] text-amber-200 border-amber-500/30">
                        {alerts.length} Active Alerts
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {alerts.map((alert) => (
                        <div key={alert.id} className="flex items-start space-x-3 p-4 border-2 border-zinc-800/60 rounded-lg bg-zinc-900/60 hover:bg-zinc-900/40 hover:border-zinc-800 transition-all duration-200">
                          <div className="flex-shrink-0 p-2 rounded-full bg-zinc-900/60">
                            {alert.type === 'opportunity' && <Eye className="h-5 w-5 text-emerald-400" />}
                            {alert.type === 'risk' && <AlertTriangle className="h-5 w-5 text-red-400" />}
                            {alert.type === 'market' && <TrendingUp className="h-5 w-5 text-amber-400" />}
                            {alert.type === 'quantum' && <Atom className="h-5 w-5 text-amber-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-zinc-200">{alert.title}</h4>
                              <div className="flex items-center space-x-2">
                                <Badge className={`${getPriorityColor(alert.priority)} border`}>
                                  {alert.priority.toUpperCase()}
                                </Badge>
                                {alert.aiGenerated && (
                                  <Badge variant="outline" className="text-xs bg-amber-500/[0.06] text-amber-300 border-zinc-800">
                                    <Brain className="h-3 w-3 mr-1" />
                                    AI Generated
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-zinc-400 mb-3 leading-relaxed">{alert.description}</p>
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center space-x-2 text-zinc-500">
                                <MapPin className="h-3 w-3" />
                                <span className="font-medium">{alert.location}</span>
                              </div>
                              <div className="flex items-center space-x-1 text-zinc-500">
                                <Clock className="h-3 w-3" />
                                <span>{new Date(alert.timestamp).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* SEC EDGAR Filing Alerts - LIVE DATA */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <FileText className="h-6 w-6 text-amber-400" />
                      <span>SEC Filing Alerts</span>
                      {secFilingsLoading ? (
                        <Badge className="bg-amber-500/[0.12] text-amber-200 border-zinc-800">
                          Loading...
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500/[0.12] text-amber-200 border-zinc-800">
                          {secFilings.length} Recent Filings
                        </Badge>
                      )}
                      <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300 border-emerald-500/30 text-xs">
                        <span className="w-2 h-2 bg-emerald-500/[0.06]0 rounded-full inline-block mr-1 animate-pulse"></span>
                        REFERENCE DATA
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {secFilingsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="h-6 w-6 text-amber-400 animate-spin" />
                      </div>
                    ) : secFilings.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No recent filings available
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {secFilings.map((filing, index) => (
                          <div key={`${filing.accession_number}-${index}`} className="flex items-start space-x-3 p-4 border-2 border-zinc-800/60 rounded-lg bg-zinc-900/60 hover:bg-zinc-900/40 hover:border-zinc-800 transition-all duration-200">
                            <div className="flex-shrink-0 p-2 rounded-full bg-amber-500/[0.12]">
                              <FileText className="h-5 w-5 text-amber-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <h4 className="text-sm font-semibold text-zinc-200">{filing.company || 'REIT'}</h4>
                                  <p className="text-xs text-zinc-400">{filing.form || 'Unknown Form'}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Badge className={`${
                                    filing.form === '10-K' ? 'bg-amber-500/[0.10] text-amber-200 border-zinc-800' :
                                    filing.form === '10-Q' ? 'bg-amber-500/[0.12] text-amber-200 border-zinc-800' :
                                    filing.form === '8-K' ? 'bg-amber-500/[0.10] text-amber-200 border-amber-500/30' :
                                    'bg-zinc-900/60 text-zinc-200 border-zinc-800'
                                  } border text-xs`}>
                                    {filing.form}
                                  </Badge>
                                </div>
                              </div>
                              <p className="text-sm text-zinc-400 mb-2 line-clamp-2">{filing.description || 'SEC Filing'}</p>
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center space-x-2 text-zinc-500">
                                  <Calendar className="h-3 w-3" />
                                  <span className="font-medium">{new Date(filing.filing_date).toLocaleDateString()}</span>
                                </div>
                                {filing.primary_document && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-xs text-amber-400 hover:text-amber-200"
                                    onClick={() => window.open(`https://www.sec.gov/cgi-bin/viewer?action=view&cik=${filing.cik}&accession_number=${filing.accession_number?.replace(/-/g, '')}&xbrl_type=v`, '_blank')}
                                  >
                                    View Filing →
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Portfolio Tab */}
              <TabsContent value="portfolio" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Portfolio Allocation */}
                  <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <PieChart className="h-6 w-6 text-amber-400" />
                        <span>Portfolio Allocation</span>
                        <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">$2.847B AUM</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={portfolioAllocation}
                              cx="50%"
                              cy="50%"
                              innerRadius={70}
                              outerRadius={120}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {portfolioAllocation.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value: any) => [`${formatCurrency(value)}`, 'Value']}
                              labelFormatter={(label) => `Asset Class: ${label}`}
                            />
                            <Legend 
                              verticalAlign="bottom" 
                              height={36}
                              formatter={(value, entry) => (
                                <span style={{ color: entry.color, fontWeight: 'bold' }}>
                                  {value}
                                </span>
                              )}
                            />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-6 space-y-3">
                        {portfolioAllocation.map((item) => (
                          <div key={item.name} className="flex items-center justify-between p-3 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center space-x-3">
                              <div 
                                className="w-4 h-4 rounded-full border-2 border-white shadow-none" 
                                style={{ backgroundColor: item.color }}
                              />
                              <div>
                                <span className="font-medium text-zinc-200">{item.name}</span>
                                <div className="text-xs text-zinc-500">{item.properties} properties</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-zinc-100">{item.percentage}%</div>
                              <div className="text-xs text-zinc-400">
                                {formatCurrency(item.value)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Geographic Distribution */}
                  <Card className="border-2 border-emerald-500/30 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <MapPin className="h-6 w-6 text-emerald-400" />
                        <span>Geographic Distribution</span>
                        <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Global Portfolio</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {geographicDistribution.map((region) => (
                          <div key={region.region} className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-semibold text-zinc-200">{region.region}</span>
                              <span className="text-lg font-bold text-emerald-400">
                                {formatCurrency(region.value)}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-xs mb-3">
                              <div className="text-center p-2 bg-amber-500/[0.08] rounded">
                                <div className="font-medium text-amber-300">{region.properties}</div>
                                <div className="text-amber-400">Properties</div>
                              </div>
                              <div className="text-center p-2 bg-emerald-500/[0.06] rounded">
                                <div className="font-medium text-emerald-300">+{region.growth}%</div>
                                <div className="text-emerald-400">YoY Growth</div>
                              </div>
                              <div className="text-center p-2 bg-amber-500/[0.08] rounded">
                                <div className="font-medium text-amber-300">{region.riskScore}/10</div>
                                <div className="text-amber-400">Risk Score</div>
                              </div>
                            </div>
                            <Progress 
                              value={(region.value / 853500000) * 100} 
                              className="h-3" 
                            />
                            <div className="mt-2 text-xs text-zinc-500 text-right">
                              {((region.value / 853500000) * 100).toFixed(1)}% of total portfolio
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Performance Attribution */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Target className="h-6 w-6 text-amber-400" />
                      <span>Performance Attribution Analysis</span>
                      <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Q3 2024</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={performanceAttribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis 
                            dataKey="factor" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            formatter={(value: any) => [`${value}%`, 'Contribution']}
                            labelStyle={{ color: '#1e293b' }}
                            contentStyle={{ 
                              backgroundColor: '#f8fafc', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar 
                            dataKey="contribution" 
                            fill="#8b5cf6"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {performanceAttribution.slice(0, 3).map((item, index) => (
                        <div key={index} className="p-3 bg-zinc-900/60 rounded-lg border shadow-none text-center">
                          <div className="text-lg font-bold text-amber-400">
                            {item.contribution > 0 ? '+' : ''}{item.contribution}%
                          </div>
                          <div className="text-sm text-zinc-400">{item.factor}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Market Trends */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <LineChart className="h-6 w-6 text-amber-400" />
                        <span>Market Trends</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart data={marketTrends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey="capRates" 
                              stroke="#3B82F6" 
                              name="Cap Rates (%)"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="occupancy" 
                              stroke="#10B981" 
                              name="Occupancy (%)"
                            />
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Transaction Volume */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <BarChart3 className="h-6 w-6 text-emerald-400" />
                        <span>Transaction Volume</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={marketTrends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="transactionVolume" fill="#10B981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Risk Tab */}
              <TabsContent value="risk" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Shield className="h-6 w-6 text-red-400" />
                      <span>Risk Management Dashboard</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-amber-400">6.8/10</div>
                        <div className="text-sm text-muted-foreground">Portfolio Risk Score</div>
                        <Progress value={68} className="mt-2" />
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-400">$127M</div>
                        <div className="text-sm text-muted-foreground">Value at Risk (95%)</div>
                        <Progress value={85} className="mt-2" />
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-amber-400">0.73</div>
                        <div className="text-sm text-muted-foreground">Sharpe Ratio</div>
                        <Progress value={73} className="mt-2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Risk Tab */}
              <TabsContent value="risk" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Market Trends */}
                  <Card className="border-2 border-amber-500/30 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <TrendingUp className="h-6 w-6 text-amber-400" />
                        <span>Market Trends Analysis</span>
                        <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Live Data</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart data={marketTrends} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fontSize: 12 }}
                              stroke="#ea580c"
                            />
                            <YAxis 
                              yAxisId="left"
                              tick={{ fontSize: 12 }}
                              stroke="#ea580c"
                            />
                            <YAxis 
                              yAxisId="right" 
                              orientation="right"
                              tick={{ fontSize: 12 }}
                              stroke="#dc2626"
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#fff7ed', 
                                border: '2px solid #fed7aa',
                                borderRadius: '12px',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                              }}
                            />
                            <Legend />
                            <Line 
                              yAxisId="left"
                              type="monotone" 
                              dataKey="capRate" 
                              stroke="#ea580c" 
                              strokeWidth={3}
                              dot={{ fill: '#ea580c', strokeWidth: 2, r: 6 }}
                              name="Cap Rate (%)"
                            />
                            <Line 
                              yAxisId="right"
                              type="monotone" 
                              dataKey="occupancy" 
                              stroke="#dc2626" 
                              strokeWidth={3}
                              dot={{ fill: '#dc2626', strokeWidth: 2, r: 6 }}
                              name="Occupancy (%)"
                            />
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-6 grid grid-cols-2 gap-4">
                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none text-center">
                          <div className="text-2xl font-bold text-amber-400">5.8%</div>
                          <div className="text-sm text-zinc-400">Avg Cap Rate</div>
                          <div className="text-xs text-emerald-400 mt-1">+0.3% vs Q2</div>
                        </div>
                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none text-center">
                          <div className="text-2xl font-bold text-red-400">92.4%</div>
                          <div className="text-sm text-zinc-400">Avg Occupancy</div>
                          <div className="text-xs text-emerald-400 mt-1">+1.2% vs Q2</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Transaction Volume */}
                  <Card className="border-2 border-teal-500/30 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <BarChart3 className="h-6 w-6 text-teal-400" />
                        <span>Transaction Volume Analysis</span>
                        <Badge variant="outline" className="bg-teal-500/[0.10] text-teal-300">YTD 2024</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={marketTrends} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#a7f3d0" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fontSize: 12 }}
                              stroke="#0d9488"
                            />
                            <YAxis 
                              tick={{ fontSize: 12 }}
                              stroke="#0d9488"
                            />
                            <Tooltip 
                              formatter={(value: any) => [`${formatCurrency(value * 1000000)}`, 'Volume']}
                              contentStyle={{ 
                                backgroundColor: '#f0fdfa', 
                                border: '2px solid #a7f3d0',
                                borderRadius: '12px',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                              }}
                            />
                            <Bar 
                              dataKey="volume" 
                              fill="#0d9488"
                              radius={[6, 6, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-6 grid grid-cols-3 gap-3">
                        <div className="p-3 bg-zinc-900/60 rounded-lg border shadow-none text-center">
                          <div className="text-lg font-bold text-teal-400">$847M</div>
                          <div className="text-xs text-zinc-400">Q3 Volume</div>
                        </div>
                        <div className="p-3 bg-zinc-900/60 rounded-lg border shadow-none text-center">
                          <div className="text-lg font-bold text-teal-400">+18.5%</div>
                          <div className="text-xs text-zinc-400">YoY Growth</div>
                        </div>
                        <div className="p-3 bg-zinc-900/60 rounded-lg border shadow-none text-center">
                          <div className="text-lg font-bold text-teal-400">127</div>
                          <div className="text-xs text-zinc-400">Transactions</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* AI Predictive Analytics */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Brain className="h-6 w-6 text-amber-400" />
                      <span>AI Predictive Analytics Engine</span>
                      <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">96.4% Accuracy</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="p-6 bg-zinc-900/60 rounded-xl border shadow-none">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="p-2 bg-emerald-500/[0.10] rounded-lg">
                            <TrendingUp className="h-6 w-6 text-emerald-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Price Prediction</div>
                            <div className="text-xs text-zinc-500">Next 12 Months</div>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-emerald-400 mb-2">+12.8%</div>
                        <div className="text-sm text-zinc-400 mb-3">Expected appreciation</div>
                        <Progress value={85} className="h-2 mb-2" />
                        <div className="text-xs text-zinc-500">Confidence: 85%</div>
                      </div>

                      <div className="p-6 bg-zinc-900/60 rounded-xl border shadow-none">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="p-2 bg-amber-500/[0.10] rounded-lg">
                            <AlertTriangle className="h-6 w-6 text-amber-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Risk Assessment</div>
                            <div className="text-xs text-zinc-500">Portfolio Risk</div>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-amber-400 mb-2">Medium</div>
                        <div className="text-sm text-zinc-400 mb-3">Risk Level: 4.2/10</div>
                        <Progress value={42} className="h-2 mb-2" />
                        <div className="text-xs text-zinc-500">VaR: $12.4M (95%)</div>
                      </div>

                      <div className="p-6 bg-zinc-900/60 rounded-xl border shadow-none">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="p-2 bg-amber-500/[0.12] rounded-lg">
                            <Target className="h-6 w-6 text-amber-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Opportunity Score</div>
                            <div className="text-xs text-zinc-500">Investment Potential</div>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-amber-400 mb-2">8.7/10</div>
                        <div className="text-sm text-zinc-400 mb-3">High opportunity</div>
                        <Progress value={87} className="h-2 mb-2" />
                        <div className="text-xs text-zinc-500">ROI Potential: 18.3%</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Risk Tab */}
              <TabsContent value="risk" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Risk Management Dashboard */}
                  <Card className="border-2 border-red-500/30 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Shield className="h-6 w-6 text-red-400" />
                        <span>Risk Management Dashboard</span>
                        <Badge variant="outline" className="bg-red-500/[0.10] text-red-300">Real-time</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-2xl font-bold text-red-400">4.2</div>
                            <div className="text-sm text-zinc-400">Portfolio Risk Score</div>
                            <div className="text-xs text-zinc-500 mt-1">Scale: 1-10</div>
                          </div>
                          <div className="text-center p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-2xl font-bold text-amber-400">$12.4M</div>
                            <div className="text-sm text-zinc-400">Value at Risk</div>
                            <div className="text-xs text-zinc-500 mt-1">95% Confidence</div>
                          </div>
                          <div className="text-center p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-2xl font-bold text-emerald-400">1.84</div>
                            <div className="text-sm text-zinc-400">Sharpe Ratio</div>
                            <div className="text-xs text-zinc-500 mt-1">Risk-Adj Return</div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-zinc-200">Credit Risk</span>
                              <span className="text-sm font-bold text-emerald-400">Low</span>
                            </div>
                            <Progress value={25} className="h-3 mb-2" />
                            <div className="text-xs text-zinc-500">Avg Credit Score: 742 | Default Probability: 0.8%</div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-zinc-200">Market Risk</span>
                              <span className="text-sm font-bold text-amber-400">Medium</span>
                            </div>
                            <Progress value={55} className="h-3 mb-2" />
                            <div className="text-xs text-zinc-500">Beta: 1.12 | Correlation: 0.78</div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-zinc-200">Liquidity Risk</span>
                              <span className="text-sm font-bold text-emerald-400">Low</span>
                            </div>
                            <Progress value={30} className="h-3 mb-2" />
                            <div className="text-xs text-zinc-500">Avg Days to Sale: 45 | Cash Ratio: 8.2%</div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-zinc-200">Operational Risk</span>
                              <span className="text-sm font-bold text-amber-400">Medium</span>
                            </div>
                            <Progress value={45} className="h-3 mb-2" />
                            <div className="text-xs text-zinc-500">Vacancy Risk: 3.2% | Maintenance Reserve: 2.1%</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Stress Testing */}
                  <Card className="border-2 border-amber-500/30 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Activity className="h-6 w-6 text-amber-400" />
                        <span>Stress Testing & Scenarios</span>
                        <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Monte Carlo</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-lg font-bold text-red-400">-18.4%</div>
                            <div className="text-sm text-zinc-400">Recession Scenario</div>
                            <div className="text-xs text-zinc-500 mt-1">Portfolio Impact</div>
                          </div>
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-lg font-bold text-amber-400">-8.7%</div>
                            <div className="text-sm text-zinc-400">Interest Rate +300bp</div>
                            <div className="text-xs text-zinc-500 mt-1">Portfolio Impact</div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Economic Downturn (2008-style)</span>
                              <Badge variant="outline" className="bg-red-500/[0.10] text-red-300">High Impact</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-xs">
                              <div className="text-center">
                                <div className="font-bold text-red-400">-22.1%</div>
                                <div className="text-zinc-500">Portfolio Value</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-red-400">-$628M</div>
                                <div className="text-zinc-500">Total Loss</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">18 months</div>
                                <div className="text-zinc-500">Recovery Time</div>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Inflation Surge (&gt;6%)</span>
                              <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Medium Impact</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-xs">
                              <div className="text-center">
                                <div className="font-bold text-amber-400">-12.8%</div>
                                <div className="text-zinc-500">Portfolio Value</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">-$364M</div>
                                <div className="text-zinc-500">Total Loss</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-emerald-400">12 months</div>
                                <div className="text-zinc-500">Recovery Time</div>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Black Swan Event</span>
                              <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Extreme</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-xs">
                              <div className="text-center">
                                <div className="font-bold text-amber-400">-35.7%</div>
                                <div className="text-zinc-500">Portfolio Value</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">-$1.02B</div>
                                <div className="text-zinc-500">Total Loss</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-red-400">36 months</div>
                                <div className="text-zinc-500">Recovery Time</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Market Tab */}
              <TabsContent value="market" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Global Market Intelligence */}
                  <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Globe className="h-6 w-6 text-amber-400" />
                        <span>Global Market Intelligence</span>
                        <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">Real-time</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-2xl font-bold text-amber-400">$4.2T</div>
                            <div className="text-sm text-zinc-400">Global CRE Volume</div>
                            <div className="text-xs text-emerald-400 mt-1">+8.4% YoY</div>
                          </div>
                          <div className="text-center p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-2xl font-bold text-emerald-400">6.8%</div>
                            <div className="text-sm text-zinc-400">Avg Cap Rate</div>
                            <div className="text-xs text-amber-400 mt-1">+0.2% vs Q2</div>
                          </div>
                          <div className="text-center p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="text-2xl font-bold text-amber-400">89.2%</div>
                            <div className="text-sm text-zinc-400">Global Occupancy</div>
                            <div className="text-xs text-emerald-400 mt-1">+1.8% vs Q2</div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">North America</span>
                              <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Strong</Badge>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-xs">
                              <div className="text-center">
                                <div className="font-bold text-emerald-400">$1.8T</div>
                                <div className="text-zinc-500">Volume</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">5.9%</div>
                                <div className="text-zinc-500">Cap Rate</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">91.4%</div>
                                <div className="text-zinc-500">Occupancy</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-emerald-400">+12.3%</div>
                                <div className="text-zinc-500">YoY Growth</div>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Europe</span>
                              <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Moderate</Badge>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-xs">
                              <div className="text-center">
                                <div className="font-bold text-emerald-400">$1.1T</div>
                                <div className="text-zinc-500">Volume</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">7.2%</div>
                                <div className="text-zinc-500">Cap Rate</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">87.8%</div>
                                <div className="text-zinc-500">Occupancy</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">+4.7%</div>
                                <div className="text-zinc-500">YoY Growth</div>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Asia Pacific</span>
                              <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Emerging</Badge>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-xs">
                              <div className="text-center">
                                <div className="font-bold text-emerald-400">$1.3T</div>
                                <div className="text-zinc-500">Volume</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">8.1%</div>
                                <div className="text-zinc-500">Cap Rate</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-amber-400">88.9%</div>
                                <div className="text-zinc-500">Occupancy</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-emerald-400">+15.8%</div>
                                <div className="text-zinc-500">YoY Growth</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Economic Indicators */}
                  <Card className="border-2 border-emerald-500/30 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Radar className="h-6 w-6 text-emerald-400" />
                        <span>Economic Indicators Impact</span>
                        <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Live Feed</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <TrendingUp className="h-4 w-4 text-emerald-400" />
                              <span className="font-medium text-zinc-200">Federal Funds Rate</span>
                            </div>
                            <span className="text-lg font-bold text-emerald-400">5.25%</span>
                          </div>
                          <div className="text-xs text-zinc-500 mb-2">Target Range: 5.25% - 5.50%</div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">Impact on CRE:</span>
                            <span className="font-medium text-amber-400">Moderate Headwind</span>
                          </div>
                        </div>

                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Activity className="h-4 w-4 text-amber-400" />
                              <span className="font-medium text-zinc-200">10-Year Treasury</span>
                            </div>
                            <span className="text-lg font-bold text-amber-400">4.68%</span>
                          </div>
                          <div className="text-xs text-zinc-500 mb-2">Change: +0.12% (1 week)</div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">Impact on CRE:</span>
                            <span className="font-medium text-red-400">Negative</span>
                          </div>
                        </div>

                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <BarChart3 className="h-4 w-4 text-amber-400" />
                              <span className="font-medium text-zinc-200">CPI Inflation</span>
                            </div>
                            <span className="text-lg font-bold text-amber-400">3.2%</span>
                          </div>
                          <div className="text-xs text-zinc-500 mb-2">YoY Change (September 2024)</div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">Impact on CRE:</span>
                            <span className="font-medium text-emerald-400">Positive</span>
                          </div>
                        </div>

                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Target className="h-4 w-4 text-amber-400" />
                              <span className="font-medium text-zinc-200">Unemployment Rate</span>
                            </div>
                            <span className="text-lg font-bold text-amber-400">3.8%</span>
                          </div>
                          <div className="text-xs text-zinc-500 mb-2">September 2024 (BLS)</div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">Impact on CRE:</span>
                            <span className="font-medium text-emerald-400">Positive</span>
                          </div>
                        </div>

                        <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Zap className="h-4 w-4 text-amber-400" />
                              <span className="font-medium text-zinc-200">GDP Growth</span>
                            </div>
                            <span className="text-lg font-bold text-amber-400">2.4%</span>
                          </div>
                          <div className="text-xs text-zinc-500 mb-2">Q3 2024 Annualized</div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">Impact on CRE:</span>
                            <span className="font-medium text-emerald-400">Strong Positive</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Market Opportunities & Alerts */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Zap className="h-6 w-6 text-amber-400" />
                      <span>AI-Powered Market Opportunities</span>
                      <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Live Alerts</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-emerald-500/[0.10] rounded-lg">
                            <TrendingUp className="h-5 w-5 text-emerald-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Industrial Surge</div>
                            <div className="text-xs text-zinc-500">Southeast Markets</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-400 mb-3">
                          AI identifies 23% cap rate compression opportunity in Atlanta, Charlotte, and Nashville industrial markets.
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-emerald-400 font-medium">High Confidence</span>
                          <span className="text-zinc-500">ROI: +24.7%</span>
                        </div>
                      </div>

                      <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-amber-500/[0.12] rounded-lg">
                            <Building className="h-5 w-5 text-amber-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Office Recovery</div>
                            <div className="text-xs text-zinc-500">Tier 1 Cities</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-400 mb-3">
                          Class A office buildings in NYC, SF, and Boston showing early signs of stabilization with flight-to-quality trends.
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-amber-400 font-medium">Medium Confidence</span>
                          <span className="text-zinc-500">ROI: +12.3%</span>
                        </div>
                      </div>

                      <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-amber-500/[0.10] rounded-lg">
                            <Home className="h-5 w-5 text-amber-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Multifamily Value-Add</div>
                            <div className="text-xs text-zinc-500">Secondary Markets</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-400 mb-3">
                          Distressed multifamily assets in Phoenix, Tampa, and Austin present significant value-add opportunities.
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-amber-400 font-medium">High Confidence</span>
                          <span className="text-zinc-500">ROI: +31.2%</span>
                        </div>
                      </div>

                      <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-red-500/[0.10] rounded-lg">
                            <AlertTriangle className="h-5 w-5 text-red-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Retail Headwinds</div>
                            <div className="text-xs text-zinc-500">Mall Properties</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-400 mb-3">
                          Regional mall properties showing accelerated distress signals. Consider exit strategies for underperforming assets.
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-red-400 font-medium">High Confidence</span>
                          <span className="text-zinc-500">Risk: -18.4%</span>
                        </div>
                      </div>

                      <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-teal-500/[0.10] rounded-lg">
                            <Zap className="h-5 w-5 text-teal-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">Data Center Boom</div>
                            <div className="text-xs text-zinc-500">AI Infrastructure</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-400 mb-3">
                          AI infrastructure demand driving unprecedented data center development opportunities in Virginia, Texas, and Oregon.
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-teal-400 font-medium">Very High Confidence</span>
                          <span className="text-zinc-500">ROI: +42.8%</span>
                        </div>
                      </div>

                      <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-amber-500/[0.10] rounded-lg">
                            <Brain className="h-5 w-5 text-amber-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-200">ESG Premium</div>
                            <div className="text-xs text-zinc-500">Green Buildings</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-400 mb-3">
                          LEED Platinum and ENERGY STAR certified properties commanding 15-20% rent premiums across all asset classes.
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-amber-400 font-medium">High Confidence</span>
                          <span className="text-zinc-500">Premium: +17.3%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Quantum Tab */}
              <TabsContent value="quantum" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Quantum Security Status */}
                  <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Atom className="h-6 w-6 text-amber-400" />
                        <span>Quantum Security Infrastructure</span>
                        <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">DIQRNG Active</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="text-center p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                          <Lock className="h-16 w-16 mx-auto mb-4 text-amber-400" />
                          <div className="text-4xl font-bold text-amber-400 mb-2">99.97%</div>
                          <div className="text-lg font-medium text-zinc-300 mb-2">Quantum Security Status</div>
                          <div className="text-sm text-zinc-500 mb-4">DIQRNG + Q-CAN Protection Active</div>
                          <Progress value={99.97} className="h-3 mb-4" />
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            <div className="text-center">
                              <div className="font-bold text-emerald-400">256-bit</div>
                              <div className="text-zinc-500">Quantum Keys</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-amber-400">0.03%</div>
                              <div className="text-zinc-500">Vulnerability</div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Shield className="h-4 w-4 text-emerald-400" />
                                <span className="font-medium text-zinc-200">DIQRNG Status</span>
                              </div>
                              <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Active</Badge>
                            </div>
                            <div className="text-sm text-zinc-400 mb-2">Device-Independent Quantum Random Number Generation</div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-zinc-500">Entropy Rate:</span>
                              <span className="font-medium text-emerald-400">1.2 Mbps</span>
                            </div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Zap className="h-4 w-4 text-amber-400" />
                                <span className="font-medium text-zinc-200">Q-CAN Protocol</span>
                              </div>
                              <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">Operational</Badge>
                            </div>
                            <div className="text-sm text-zinc-400 mb-2">Quantum-Resistant Cryptographic Authentication Network</div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-zinc-500">Resistance Level:</span>
                              <span className="font-medium text-amber-400">Post-Quantum</span>
                            </div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Activity className="h-4 w-4 text-amber-400" />
                                <span className="font-medium text-zinc-200">Quantum Threat Detection</span>
                              </div>
                              <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Monitoring</Badge>
                            </div>
                            <div className="text-sm text-zinc-400 mb-2">Real-time quantum attack pattern analysis</div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-zinc-500">Threats Detected:</span>
                              <span className="font-medium text-emerald-400">0 (24h)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* AI Model Performance */}
                  <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Brain className="h-6 w-6 text-amber-400" />
                        <span>AI Model Performance</span>
                        <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">Real-time</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="text-center p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                          <Brain className="h-16 w-16 mx-auto mb-4 text-amber-400" />
                          <div className="text-4xl font-bold text-amber-400 mb-2">97.3%</div>
                          <div className="text-lg font-medium text-zinc-300 mb-2">Overall Model Accuracy</div>
                          <div className="text-sm text-zinc-500 mb-4">Ensemble AI Prediction Engine</div>
                          <Progress value={97.3} className="h-3 mb-4" />
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            <div className="text-center">
                              <div className="font-bold text-emerald-400">847ms</div>
                              <div className="text-zinc-500">Avg Response</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-amber-400">12.4M</div>
                              <div className="text-zinc-500">Predictions/Day</div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Property Valuation Model</span>
                              <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">98.7%</Badge>
                            </div>
                            <Progress value={98.7} className="h-2 mb-2" />
                            <div className="text-xs text-zinc-500">Trained on 2.4M property transactions</div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Market Trend Prediction</span>
                              <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">94.2%</Badge>
                            </div>
                            <Progress value={94.2} className="h-2 mb-2" />
                            <div className="text-xs text-zinc-500">Economic indicator correlation analysis</div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Risk Assessment Engine</span>
                              <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">91.8%</Badge>
                            </div>
                            <Progress value={91.8} className="h-2 mb-2" />
                            <div className="text-xs text-zinc-500">Multi-factor risk modeling</div>
                          </div>

                          <div className="p-4 bg-zinc-900/60 rounded-lg border shadow-none">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-zinc-200">Opportunity Detection</span>
                              <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">96.1%</Badge>
                            </div>
                            <Progress value={96.1} className="h-2 mb-2" />
                            <div className="text-xs text-zinc-500">Pattern recognition & anomaly detection</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quantum-Enhanced AI Analytics */}
                <Card className="border-2 border-zinc-800 bg-zinc-900/40">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Zap className="h-6 w-6 text-amber-400" />
                      <span>Quantum-Enhanced AI Analytics</span>
                      <Badge variant="outline" className="bg-amber-500/[0.10] text-amber-200">Next-Gen</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="text-center p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="p-3 bg-emerald-500/[0.10] rounded-full w-fit mx-auto mb-4">
                          <Target className="h-8 w-8 text-emerald-400" />
                        </div>
                        <div className="text-2xl font-bold text-emerald-400 mb-2">99.4%</div>
                        <div className="text-sm font-medium text-zinc-300 mb-1">Quantum Precision</div>
                        <div className="text-xs text-zinc-500">Property value predictions</div>
                      </div>

                      <div className="text-center p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="p-3 bg-amber-500/[0.12] rounded-full w-fit mx-auto mb-4">
                          <Radar className="h-8 w-8 text-amber-400" />
                        </div>
                        <div className="text-2xl font-bold text-amber-400 mb-2">847ms</div>
                        <div className="text-sm font-medium text-zinc-300 mb-1">Quantum Speed</div>
                        <div className="text-xs text-zinc-500">Complex analysis time</div>
                      </div>

                      <div className="text-center p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="p-3 bg-amber-500/[0.10] rounded-full w-fit mx-auto mb-4">
                          <Globe className="h-8 w-8 text-amber-400" />
                        </div>
                        <div className="text-2xl font-bold text-amber-400 mb-2">2.4M</div>
                        <div className="text-sm font-medium text-zinc-300 mb-1">Data Points</div>
                        <div className="text-xs text-zinc-500">Real-time processing</div>
                      </div>

                      <div className="text-center p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="p-3 bg-amber-500/[0.10] rounded-full w-fit mx-auto mb-4">
                          <TrendingUp className="h-8 w-8 text-amber-400" />
                        </div>
                        <div className="text-2xl font-bold text-amber-400 mb-2">156%</div>
                        <div className="text-sm font-medium text-zinc-300 mb-1">Performance Gain</div>
                        <div className="text-xs text-zinc-500">vs Classical AI</div>
                      </div>
                    </div>

                    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-4">
                          <Atom className="h-5 w-5 text-amber-400" />
                          <span className="font-semibold text-zinc-200">Quantum Advantage Applications</span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Portfolio Optimization</span>
                            <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Active</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Risk Correlation Analysis</span>
                            <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Active</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Market Simulation</span>
                            <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">Processing</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Predictive Modeling</span>
                            <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Active</Badge>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 bg-zinc-900/60 rounded-lg border shadow-none">
                        <div className="flex items-center space-x-2 mb-4">
                          <Shield className="h-5 w-5 text-amber-400" />
                          <span className="font-semibold text-zinc-200">Security & Compliance</span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Data Encryption</span>
                            <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Quantum-Safe</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Transaction Security</span>
                            <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">Q-CAN Protected</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Audit Trail</span>
                            <Badge variant="outline" className="bg-amber-500/[0.12] text-amber-200">Immutable</Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Compliance Status</span>
                            <Badge variant="outline" className="bg-emerald-500/[0.10] text-emerald-300">SOC 2 Type II</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Detailed Tab */}
              <TabsContent value="detailed" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredMetrics.map((metric) => {
                    const IconComponent = metric.icon
                    return (
                      <Card key={metric.id} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <IconComponent className="h-5 w-5" />
                            <span>{metric.title}</span>
                            <Badge className={`ml-auto ${getStatusColor(metric.status)}`}>
                              {metric.status}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <div className="text-3xl font-bold">{metric.value}</div>
                              <div className="text-sm text-muted-foreground">{metric.description}</div>
                            </div>
                            
                            {metric.benchmark && (
                              <div className="flex items-center justify-between text-sm">
                                <span>Benchmark:</span>
                                <span className="font-medium">{metric.benchmark}</span>
                              </div>
                            )}
                            
                            {metric.ytdChange && (
                              <div className="flex items-center justify-between text-sm">
                                <span>YTD Change:</span>
                                <span className={`font-medium ${metric.ytdChange > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {metric.ytdChange > 0 ? '+' : ''}{metric.ytdChange}%
                                </span>
                              </div>
                            )}
                            
                            {metric.volatility && (
                              <div className="flex items-center justify-between text-sm">
                                <span>Volatility:</span>
                                <span className="font-medium">{metric.volatility}%</span>
                              </div>
                            )}
                            
                            {metric.confidence && (
                              <div>
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span>Confidence:</span>
                                  <span className="font-medium">{metric.confidence}%</span>
                                </div>
                                <Progress value={metric.confidence} className="h-2" />
                              </div>
                            )}
                            
                            {metric.aiInsight && (
                              <div className="p-3 bg-amber-500/[0.08] rounded-lg">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Brain className="h-4 w-4 text-amber-400" />
                                  <span className="font-medium text-amber-400 text-sm">AI Insight</span>
                                </div>
                                <p className="text-sm text-amber-300">{metric.aiInsight}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </TabsContent>
            </motion.div>
          </AnimatePresence>
        </Tabs>
      </div>
    </div>
  )
}

export default UserDashboard
